import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";

import SavingsPotCalculatorResult from "./SavingsPotCalculatorResult";
import OmInputFieldWrapper from "../forms/OmInputFieldWrapper";
import OmButton from "../forms/OmButton";
import { createActionHandler } from "../../util";
import FieldDetailModal from "./FieldDetailModal";
import "./SavingsPotCalculator.scss";
import { OmLoader, OmOverlayOpener, OmIcon } from "@om/component-library-react";
import { getScrollToClass } from "@om/redux/selectors/savingsPotCalculator/scrolling";
import {
  getRetirementAmount,
  getMonthlyIncome,
  getShowResultSummary,
  getWithdrawalGrossAmount,
  getWithdrawalNetAmount,
  getWithdrawalNetAmountRaw,
  getWithdrawalGrossAmountRaw,
  getTaxAmount,
  getButtonDisabledValue,
  getShowLoader,
  getApiError,
  getShowFieldDetailsStatus,
  getInputDisabled,
  getDisplayReset,
} from "@om/redux/selectors/savingsPotCalculator/savingsPotCalculatorForm";
import {
  setRetirementAmountValue,
  setMonthlyIncomeValue,
  showFieldDetails,
  resetCalculatorForm,
} from "@om/redux/actions/savingsPotCalculator/savingsPotCalculatorForm";

const SavingsCalculatorForm = ({
  twoColumnImageLeftContent,
  tableContent,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const {
    retirementAmount,
    monthlyIncome,
    showResultSummary,
    isButtonDisabled,
    withdrawalGrossAmount,
    withdrawalNetAmount,
    withdrawalNetAmountRaw,
    withdrawalGrossAmountRaw,
    taxAmount,
    showLoader,
    showFieldDetailsStatus,
    apiError,
    inputDisabled,
    displayReset,
  } = useSelector(
    createStructuredSelector({
      scrollToClass: getScrollToClass,
      retirementAmount: getRetirementAmount,
      monthlyIncome: getMonthlyIncome,
      showResultSummary: getShowResultSummary,
      isButtonDisabled: getButtonDisabledValue,
      withdrawalGrossAmount: getWithdrawalGrossAmount,
      withdrawalNetAmount: getWithdrawalNetAmount,
      withdrawalNetAmountRaw: getWithdrawalNetAmountRaw,
      withdrawalGrossAmountRaw: getWithdrawalGrossAmountRaw,
      taxAmount: getTaxAmount,
      showLoader: getShowLoader,
      apiError: getApiError,
      inputDisabled: getInputDisabled,
      displayReset: getDisplayReset,
      showFieldDetailsStatus: getShowFieldDetailsStatus,
    })
  );
  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ retirementAmount, monthlyIncome });
  };

  const onKeyDown = (keyEvent) => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  return (
    <form
      className="savings-pot-calculator-wrapper"
      onSubmit={handleSubmit}
      onKeyDown={onKeyDown}
    >
      <div className="row">
        <div className="savings-pot-calculator-form-column-one">
          <OmInputFieldWrapper
            id="retirementAmount"
            size="full"
            required="true"
            name="retirementAmount"
            label="How much have you saved towards your retirement (one calculation per investment)?"
            labelAlwaysShow={true}
            keyboardMode="numeric"
            mask="thousands-spaced"
            placeholder="e.g 5000"
            prefixString="R"
            disabled={inputDisabled}
            reduxValue={retirementAmount}
            persistHandler={createActionHandler(
              dispatch,
              setRetirementAmountValue
            )}
          />
        </div>
        <div className="savings-pot-calculator-form-column-two">
          <span className="advanced-input-field">
            <OmInputFieldWrapper
              id="monthlyIncome"
              size="full"
              required="true"
              name="monthlyIncome"
              label="What is your monthly income before deductions (for tax purposes)?"
              labelAlwaysShow={true}
              keyboardMode="numeric"
              mask="thousands-spaced"
              placeholder="e.g 5000"
              prefixString="R"
              disabled={inputDisabled}
              reduxValue={monthlyIncome}
              persistHandler={createActionHandler(
                dispatch,
                setMonthlyIncomeValue
              )}
            />
            <span className="field-more-details monthly-income-popup ">
              <OmOverlayOpener openingOverlay="field-details">
                <OmIcon
                  iconName="error_outline"
                  onClick={() =>
                    dispatch(
                      showFieldDetails({
                        field: "monthlyIncome",
                        subField: "income",
                        title: "Your monthly income",
                      })
                    )
                  }
                />
              </OmOverlayOpener>
            </span>
          </span>
        </div>
        <FieldDetailModal
          title={
            showFieldDetailsStatus && showFieldDetailsStatus.title
              ? showFieldDetailsStatus.title
              : ""
          }
          content={
            showFieldDetailsStatus && showFieldDetailsStatus.fieldDescription
              ? showFieldDetailsStatus.fieldDescription
              : ""
          }
        />
      </div>

      <div className="button-results-wrapper">
        <div className="button-wrapper">
          <div className="reset-calculate-button">
            {displayReset && (
              <OmButton
                slot="recalculate-mobile"
                type="secondary"
                size="small"
                text="RESET CALCULATOR"
                withIcon={false}
                onClick={() => dispatch(resetCalculatorForm())}
              />
            )}
          </div>

          <div className="reset-calculate-button-mobile">
            {displayReset && (
              <OmButton
                slot="recalculate-mobile"
                type="secondary"
                size="small"
                text="RESET CALCULATOR"
                withIcon={false}
                onClick={() => dispatch(resetCalculatorForm())}
              />
            )}
          </div>

          <div className="calculate-button-mobile">
            <OmButton
              slot="button"
              type="primary"
              size="small"
              text="CALCULATE"
              iconName="arrow_forward"
              withIcon={true}
              disabled={isButtonDisabled}
              onClick={() => handleSubmit}
              style={{ marginTop: "65px", display: "inline-block" }}
            />
          </div>
          <div className="calculate-button-web">
            <OmButton
              slot="button"
              type="primary"
              size="small"
              text="CALCULATE"
              iconName="arrow_forward"
              withIcon={true}
              disabled={isButtonDisabled}
              onClick={() => handleSubmit}
            />
          </div>
        </div>

        {showLoader ? (
          <OmLoader isLoading={true} />
        ) : (
          <SavingsPotCalculatorResult
            showResult={showResultSummary}
            withdrawalGrossAmount={withdrawalGrossAmount}
            withdrawalNetAmount={withdrawalNetAmount}
            withdrawalNetAmountRaw={withdrawalNetAmountRaw}
            withdrawalGrossAmountRaw={withdrawalGrossAmountRaw}
            taxAmount={taxAmount}
            twoColumnImageLeftContent={twoColumnImageLeftContent}
            tableContent={tableContent}
            apiError={apiError}
          />
        )}
      </div>
    </form>
  );
};

export default SavingsCalculatorForm;
